import React, { useState } from "react";
import { Bug, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/auth";
import BugReportDialog from "./BugReportDialog";
import TeacherFeedbackChoiceDialog, {
  type TeacherFeedbackChoice,
} from "./TeacherFeedbackChoiceDialog";
import IdeaSuggestionDialog from "./IdeaSuggestionDialog";

/**
 * Floating feedback button that appears for authenticated users.
 * - Students: opens the bug report dialog directly.
 * - Teachers: opens a choice dialog (report a bug / suggest an idea); the
 *   "idea" option opens a dialog with the shared Padlet board.
 */
const BugReportButton: React.FC = () => {
  const { t } = useLanguage();
  const { user, profile } = useAuth();

  const [isBugDialogOpen, setIsBugDialogOpen] = useState(false);
  const [isChoiceDialogOpen, setIsChoiceDialogOpen] = useState(false);
  const [isIdeaDialogOpen, setIsIdeaDialogOpen] = useState(false);

  if (!user) {
    return null;
  }

  const isTeacher = profile?.role === "teacher";

  const tooltipLabel = isTeacher
    ? t("Nahlásit chybu nebo navrhnout nápad", "Nahlásiť chybu alebo navrhnúť nápad")
    : t("Nahlásit chybu", "Nahlásiť chybu");

  const handleButtonClick = () => {
    if (isTeacher) {
      setIsChoiceDialogOpen(true);
    } else {
      setIsBugDialogOpen(true);
    }
  };

  const handleChoiceSelect = (choice: TeacherFeedbackChoice) => {
    setIsChoiceDialogOpen(false);
    if (choice === "bug") {
      setIsBugDialogOpen(true);
    } else {
      setIsIdeaDialogOpen(true);
    }
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 z-50 sm:bottom-6 sm:right-6">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size={isTeacher ? "default" : "icon"}
              onClick={handleButtonClick}
              className={
                isTeacher
                  ? "h-10 gap-1.5 rounded-full px-3 shadow-lg bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background hover:border-primary/50 hover:text-primary transition-all duration-200"
                  : "h-10 w-10 rounded-full shadow-lg bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background hover:border-primary/50 hover:text-primary transition-all duration-200"
              }
              aria-label={tooltipLabel}
            >
              {isTeacher ? (
                <>
                  <Bug className="h-4 w-4" aria-hidden="true" />
                  <span className="text-muted-foreground/70" aria-hidden="true">
                    /
                  </span>
                  <Lightbulb className="h-4 w-4" aria-hidden="true" />
                </>
              ) : (
                <Bug className="h-5 w-5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={8}>
            <p>{tooltipLabel}</p>
          </TooltipContent>
        </Tooltip>
      </div>

      <BugReportDialog open={isBugDialogOpen} onOpenChange={setIsBugDialogOpen} />

      {isTeacher && (
        <>
          <TeacherFeedbackChoiceDialog
            open={isChoiceDialogOpen}
            onOpenChange={setIsChoiceDialogOpen}
            onSelect={handleChoiceSelect}
          />
          <IdeaSuggestionDialog open={isIdeaDialogOpen} onOpenChange={setIsIdeaDialogOpen} />
        </>
      )}
    </>
  );
};

export default BugReportButton;
